import { useState, useEffect } from "react";
import { API, useAuth , authFetch } from "@/App";
import {
  BarChart3, TrendingUp, Award, Target, ArrowUp, ArrowDown, Minus
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const pctColor = (pct) => {
  if (pct >= 100) return "text-emerald-600";
  if (pct >= 75) return "text-blue-600";
  if (pct >= 50) return "text-amber-600";
  return "text-red-600";
};

const pctBg = (pct) => {
  if (pct >= 100) return "bg-emerald-500";
  if (pct >= 75) return "bg-blue-500";
  if (pct >= 50) return "bg-amber-500";
  return "bg-red-500";
};

const SchemeAchievementsPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState(user?.region_id || "ALL");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = region !== "ALL" ? `?region=${region}` : "";
        const res = await authFetch(`${API}/schemes/achievements${params}`, {  });
        const json = await res.json();
        if (json.success) setData(json.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [region]);

  const summary = data?.summary || {};
  const schemes = data?.schemes || [];

  return (
    <div className="space-y-6" data-testid="scheme-achievements-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Scheme Achievements</h1>
          <p className="text-sm text-slate-500 mt-1">Performance infographics across all active schemes</p>
        </div>
        {user?.role === "SUPER_ADMIN" && (
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="w-[120px] h-9" data-testid="region-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Regions</SelectItem>
              <SelectItem value="GJ">Gujarat</SelectItem>
              <SelectItem value="RJ">Rajasthan</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-[#8B1A1A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="achievement-summary">
            <Card className="p-4">
              <p className="text-[10px] uppercase text-slate-400 font-semibold">Total Schemes</p>
              <p className="text-2xl font-bold font-mono text-slate-900 mt-1">{summary.total_schemes}</p>
            </Card>
            <Card className="p-4">
              <p className="text-[10px] uppercase text-slate-400 font-semibold">Active</p>
              <p className="text-2xl font-bold font-mono text-emerald-600 mt-1">{summary.active_schemes}</p>
            </Card>
            <Card className="p-4">
              <p className="text-[10px] uppercase text-slate-400 font-semibold">Avg Achievement</p>
              <p className={`text-2xl font-bold font-mono mt-1 ${pctColor(summary.avg_achievement_pct)}`}>{summary.avg_achievement_pct}%</p>
            </Card>
            <Card className="p-4">
              <p className="text-[10px] uppercase text-slate-400 font-semibold">Above Target</p>
              <p className="text-2xl font-bold font-mono text-emerald-600 mt-1 flex items-center gap-1">
                <ArrowUp className="w-4 h-4" /> {summary.above_target}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-[10px] uppercase text-slate-400 font-semibold">Below 50%</p>
              <p className="text-2xl font-bold font-mono text-red-600 mt-1 flex items-center gap-1">
                <ArrowDown className="w-4 h-4" /> {summary.below_50}
              </p>
            </Card>
          </div>

          {/* Scheme Cards with Progress Bars */}
          {schemes.length === 0 ? (
            <Card className="p-8 text-center text-slate-400">
              <BarChart3 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">No schemes found</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {schemes.map(scheme => (
                <Card key={scheme.scheme_id} className="p-5 hover:shadow-md transition-shadow" data-testid={`scheme-card-${scheme.scheme_id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">{scheme.scheme_name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-[9px] px-1.5 py-0">{scheme.scheme_type}</Badge>
                        <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${scheme.status === "ACTIVE" ? "text-emerald-600 border-emerald-200 bg-emerald-50" : "text-slate-500"}`}>
                          {scheme.status}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-xl font-bold font-mono ${pctColor(scheme.achievement_pct)}`}>
                        {scheme.achievement_pct}%
                      </p>
                      <p className="text-[9px] text-slate-400">achieved</p>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mb-3">
                    <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${pctBg(scheme.achievement_pct)}`}
                        style={{ width: `${Math.min(scheme.achievement_pct, 100)}%` }} />
                    </div>
                    <div className="flex items-center justify-between mt-1.5 text-[10px] text-slate-500">
                      <span>Achieved: <span className="font-mono font-medium text-slate-700">{(scheme.achieved_value / 100).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}</span></span>
                      <span>Target: <span className="font-mono font-medium text-slate-700">{(scheme.target_value / 100).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}</span></span>
                    </div>
                  </div>

                  {/* Details row */}
                  <div className="grid grid-cols-3 gap-2 text-[10px] pt-3 border-t border-slate-100">
                    <div>
                      <p className="text-slate-400">Period</p>
                      <p className="font-mono text-slate-700">{scheme.start_date} — {scheme.end_date}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Eligible</p>
                      <p className="font-mono font-medium text-slate-700">{scheme.total_eligible}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Reward</p>
                      <p className="font-mono font-medium text-slate-700">
                        {scheme.reward_type === "CASH" ? `INR ${scheme.reward_value?.toLocaleString()}` : scheme.reward_value || "—"}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SchemeAchievementsPage;
